import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  IconButton,
  Checkbox,
  FormControlLabel,
  Divider,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoIcon from '@mui/icons-material/Info';
import { useTranslation } from 'react-i18next';
import LanguageSelect from '../shared/LanguageSelect';
import axios from 'axios';
import { useSubscriptionStore } from '../../store';

function WorkOrderConfirmationDialog({ open, handleClose, maintenance, setMaintenance }) {
  const { t } = useTranslation();
  const { subscriptionInfo, fetchSubscriptionInfo, canUseSms } = useSubscriptionStore();
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSms, setSendSms] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [language, setLanguage] = useState(maintenance?.customer?.preferred_language || 'fi');
  const [result, setResult] = useState(null);

  const smsEnabled = canUseSms();
  const isTrial = subscriptionInfo?.is_trial;

  useEffect(() => {
    if (open && maintenance?.id) {
      setEmail(maintenance.customer?.email || '');
      setPhone(maintenance.customer?.phone || '');
      setLanguage(maintenance.customer?.preferred_language || 'fi');
      setResult(null);
      setMessage('');
      if (!subscriptionInfo) {
        fetchSubscriptionInfo();
      }
    }
  }, [open, maintenance?.id]);

  const handleSend = async () => {
    setSending(true);
    setResult(null);
    try {
      const response = await axios.post(
        `/api/maintenances/${maintenance.id}/request_confirmation/`,
        {
          message,
          send_email: sendEmail,
          send_sms: sendSms,
          email: email || undefined,
          phone: phone || undefined,
          language,
        }
      );
      setResult(response.data);
      setMaintenance(response.data.maintenance);
    } catch (err) {
      setResult({ error: err.response?.data?.error || t('confirmation.confirmationRequestError') });
    } finally {
      setSending(false);
    }
  };

  const handleCancel = () => {
    setResult(null);
    setMessage('');
    handleClose();
  };

  const canSend = (sendEmail || sendSms) && (!sendEmail || email) && (!sendSms || phone);

  // Success state
  if (result && !result.error) {
    return (
      <Dialog
        open={open}
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogContent sx={{ textAlign: 'center', py: 5 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 56, color: 'success.main', mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            {t('confirmation.confirmationRequestSent')}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
            {result.email_sent && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main' }}>
                <EmailIcon fontSize="small" />
                <Typography variant="body2">{t('Email')}</Typography>
              </Box>
            )}
            {result.sms_sent && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main' }}>
                <SmsIcon fontSize="small" />
                <Typography variant="body2">{t('SMS')}</Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancel} variant="contained" fullWidth sx={{ borderRadius: '10px' }}>
            {t('Close')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px' } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CheckCircleOutlineIcon sx={{ color: 'primary.main' }} />
          <Typography sx={{ fontWeight: 600, fontSize: '18px' }}>
            {t('confirmation.workOrderConfirmation')}
          </Typography>
        </Box>
        <IconButton onClick={handleCancel} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {result?.error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: '8px' }}>
            {result.error}
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 3, borderRadius: '8px' }}>
          {t('confirmation.customerReceivesLink')}
        </Alert>

        {/* Message */}
        <TextField
          fullWidth
          multiline
          rows={3}
          label={t('confirmation.messageToCustomer')}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={t('confirmation.messagePlaceholder')}
          sx={{ mb: 3 }}
        />

        <Divider sx={{ my: 2 }} />

        {/* Send Options */}
        <Typography sx={{ fontWeight: 600, mb: 2 }}>{t('Send notification')}</Typography>

        {/* Language selector */}
        <LanguageSelect
          size="small"
          sx={{ mb: 2, minWidth: 200, display: 'block', '& .MuiInputBase-root': { minWidth: 200 } }}
          value={language}
          label={t('Message language')}
          onChange={(e) => setLanguage(e.target.value)}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon sx={{ fontSize: 20 }} />
                <Typography>{t('Send via email')}</Typography>
              </Box>
            }
          />
          {sendEmail && (
            <TextField
              fullWidth
              size="small"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('Email address')}
              sx={{ mb: 1, ml: 4, width: 'calc(100% - 32px)' }}
            />
          )}

          <Tooltip
            title={!smsEnabled && isTrial ? t('SMS is not available during trial') : ''}
            placement="right"
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={sendSms}
                  onChange={(e) => setSendSms(e.target.checked)}
                  disabled={!smsEnabled}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SmsIcon sx={{ fontSize: 20, color: smsEnabled ? 'inherit' : 'text.disabled' }} />
                  <Typography sx={{ color: smsEnabled ? 'inherit' : 'text.disabled' }}>
                    {t('Send via SMS')}
                  </Typography>
                  {!smsEnabled && isTrial && (
                    <Tooltip title={t('SMS is not available during trial')}>
                      <InfoIcon sx={{ fontSize: 16, color: 'info.main', ml: 0.5 }} />
                    </Tooltip>
                  )}
                </Box>
              }
            />
          </Tooltip>
          {!smsEnabled && isTrial && (
            <Alert severity="info" sx={{ mb: 2, ml: 4, width: 'calc(100% - 32px)', borderRadius: '8px' }}>
              {t('SMS notifications are available with a paid subscription. Upgrade to send SMS messages to customers.')}
            </Alert>
          )}
          {sendSms && (
            <TextField
              fullWidth
              size="small"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={t('Phone number')}
              sx={{ mb: 1, ml: 4, width: 'calc(100% - 32px)' }}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleCancel} sx={{ borderRadius: '10px' }}>
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={sending || !canSend}
          startIcon={sending ? <CircularProgress size={18} /> : <CheckCircleOutlineIcon />}
          sx={{ borderRadius: '10px' }}
        >
          {t('confirmation.sendConfirmationRequest')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default WorkOrderConfirmationDialog;
